import { PORTFOLIO_DATA } from '../shared/data.js';
import { renderNav, initNavigation } from '../shared/navigation.js';
import { renderFooter, initFooter } from '../shared/footer.js';
import '../style.css';

const ABOUT = [
  "I am He ShaoLiang, a Computer Engineering undergraduate at the National University of Singapore, with an exchange semester at the Technical University of Denmark covering image analysis, computer vision, machine learning, and autonomous marine robotics.",
  `I design software systems for autonomous vehicles in international competitions, including RoboSub 2025, RobotX 2024, and the Singapore Autonomous Underwater Challenge (SAUVC). You can see some of our vehicles at <a href="https://bumblebee.sg/" target="_blank" rel="noopener">bumblebee.sg</a>.`, 
  "Before my software phase, I worked on both mechanical and electrical design. That meant building a 3D printer from scratch, doing CAD and fabrication, then moving into PCB design and embedded firmware over CAN bus, I²C, SPI, and UART. Moving up the stack from bolts to bits is what lets me reason about a robot end to end."
];

const JOURNEY = [
  { id: "mech", label: "MECHANICAL", period: "2018 to 2020", tag: "THE FRAME",
    items: ["3D printer from scratch", "CAD & fabrication in Fusion 360", "VEX, RoboCup Junior chassis"] },
  { id: "elec", label: "ELECTRICAL", period: "2020 to 2023", tag: "THE NERVES",
    items: ["PCB design", "CAN Bus · I²C · SPI · UART", "Embedded C/C++ on AVR, STM32, ESP32"] },
  { id: "soft", label: "SOFTWARE", period: "2022 to now", tag: "THE MIND",
    items: ["ROS 2 · PX4 · ArduPilot · MAVROS", "Computer vision · SLAM · pose estimation", "Behaviour trees · Zenoh pub/sub"] }
];

const STACK = PORTFOLIO_DATA.skills.slice(0, 5).map(s => ({
  group: s.category.toUpperCase(),
  items: s.items
}));

const XP = PORTFOLIO_DATA.experience.map(e => ({
  company: e.company,
  role: e.role,
  period: e.period.replace(/\s*[–—-]\s*/g, ' to '),
  place: e.company === 'Bumblebee Autonomous Systems' ? 'NUS · Singapore' : 'Singapore',
  blurb: e.description.split('. ').slice(0, 1).join('. ') + '.',
  bullets: e.highlights
}));

function render() {
  const main = document.getElementById('aboutPage');
  if (!main) return;

  main.innerHTML = `
    <section class="about-lead">
      <a href="index.html#top" class="pd-back-link">← Back home</a>
      <div class="section-head">
        <div class="section-num">01</div>
        <div class="section-title-wrap">
          <div class="section-kicker">WHO I AM</div>
          <h2 class="section-title">ABOUT&nbsp;<em>ME.</em></h2>
        </div>
      </div>

      <div class="about-card">
        <div class="about-head">
          <div class="about-avatar">
            <div class="about-avatar-inner">HE</div>
            <img src="beach.jpeg" alt="He ShaoLiang" class="about-avatar-img" onerror="this.style.display='none'" />
            <div class="about-avatar-frame"></div>
          </div>
        </div>
        <div class="about-body">
          <div class="about-caveat">Hi, I'm ShaoLiang.</div>
          ${ABOUT.map(p => `<p>${p}</p>`).join('')}
        </div>
      </div>
    </section>

    <section class="journey">
      <div class="section-head">
        <div class="section-num">02</div>
        <div class="section-title-wrap">
          <div class="section-kicker">THE ARC</div>
          <h2 class="section-title">FROM&nbsp;<em>METAL</em>&nbsp;TO&nbsp;<em>CODE</em>.</h2>
          <div class="section-desc">A full-stack robotics engineer isn't born. It's seven years of moving up the layers, from bolts to bits.</div>
        </div>
      </div>
      <div class="journey-rail">
        ${JOURNEY.map(j => `
          <div class="journey-node" data-id="${j.id}">
            <div class="journey-badge">${j.label}</div>
            <div class="journey-label">${j.label.slice(0, 4)}.</div>
            <div class="journey-period">${j.period}</div>
            <div class="journey-tag">${j.tag}</div>
            <ul class="journey-items">${j.items.map(it => `<li>${it}</li>`).join('')}</ul>
          </div>
        `).join('')}
      </div>
    </section>

    <section class="xp">
      <div class="section-head">
        <div class="section-num">03</div>
        <div class="section-title-wrap">
          <div class="section-kicker">EXPERIENCE</div>
          <h2 class="section-title">ON&nbsp;<em>PAYROLL.</em></h2>
          <div class="section-desc">Where I've shipped for real.</div>
        </div>
      </div>
      <div class="xp-list">
        ${XP.map(x => `
          <article class="xp-row">
            <div class="xp-meta">
              <div class="xp-period">${x.period}</div>
              <div class="xp-co">${x.company}</div>
              <div class="xp-place">${x.place}</div>
            </div>
            <div>
              <div class="xp-role">${x.role}</div>
              <p class="xp-blurb">${x.blurb}</p>
              <ul class="xp-bullets">${x.bullets.map(b => `<li>${b}</li>`).join('')}</ul>
            </div>
          </article>
        `).join('')}
      </div>
    </section>

    <section class="stack">
      <div class="section-head">
        <div class="section-num">04</div>
        <div class="section-title-wrap">
          <div class="section-kicker">TOOLS OF THE TRADE</div>
          <h2 class="section-title">THE&nbsp;<em>STACK.</em></h2>
          <div class="section-desc">What I reach for. Grouped by layer.</div>
        </div>
      </div>
      <div class="stack-grid">
        ${STACK.map((s, i) => `
          <div class="stack-card">
            <div class="stack-card-head">
              <div class="stack-group">${s.group}</div>
              <div class="stack-num">0${i + 1}</div>
            </div>
            <div class="stack-items">${s.items.map(it => `<span class="stack-item">${it}</span>`).join('')}</div>
          </div>
        `).join('')}
      </div>
    </section>
  `;
}

document.addEventListener('DOMContentLoaded', () => {
  renderNav();
  initNavigation();
  render();
  renderFooter();
  initFooter();
});
